<template>
  <div class="family-home w-full">
    <!-- 顶部问候（固定） -->
    <header
      class="family-tab-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center justify-between">
        <div class="flex-1 min-w-0">
          <p class="text-xs text-slate-400">您好</p>
          <p class="text-base font-semibold text-slate-800 truncate">
            {{ userName }}，需要专业陪护吗？
          </p>
        </div>
        <button
          type="button"
          class="flex-shrink-0 w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-400"
          aria-label="通知"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>
      </div>
    </header>

    <div class="family-tab-scroll">
    <div class="px-4 pt-4 pb-6 family-home-scroll-pad">
      <!-- Hero：全幅人物图 + 左侧渐变文案 -->
      <div
        class="hero-banner relative overflow-hidden rounded-2xl mb-5 shadow-[0_12px_40px_rgb(0,130,212,0.18)]"
      >
        <img
          :src="heroBanner"
          alt="专业护工一对一陪护"
          class="hero-banner-img absolute inset-0 w-full h-full"
        />
        <div
          class="absolute inset-0 bg-gradient-to-r from-[#005FA3] from-0% via-[#0078C8]/88 via-[42%] to-transparent to-72%"
        />
        <div class="relative z-10 flex flex-col justify-center hero-banner-text px-5 py-5 max-w-[62%]">
          <p class="text-xl font-bold text-white leading-snug">7×24 小时</p>
          <p class="text-sm font-bold text-white mt-1 leading-snug">一对一专业陪护</p>
          <p class="text-[11px] text-white/90 mt-1.5 leading-relaxed">
            人工调度 · 实拍打卡 · 全程可追踪
          </p>
        </div>
      </div>

      <!-- 数据概览 -->
      <div class="grid grid-cols-3 gap-2.5 mb-5">
        <div
          v-for="stat in stats"
          :key="stat.label"
          class="bg-white rounded-xl py-3.5 px-2 text-center shadow-[0_2px_12px_rgb(0,0,0,0.04)]"
        >
          <p class="text-lg font-bold text-family-blue leading-none">
            {{ stat.value }}<span class="text-xs font-semibold">{{ stat.unit }}</span>
          </p>
          <p class="text-[11px] text-slate-400 mt-1.5">{{ stat.label }}</p>
        </div>
      </div>

      <!-- 服务亮点 -->
      <div class="grid grid-cols-3 gap-2.5 mb-6">
        <div
          v-for="item in highlights"
          :key="item.title"
          class="rounded-xl p-3 text-center"
          :class="item.color"
        >
          <div class="w-9 h-9 mx-auto rounded-lg bg-white/70 flex items-center justify-center mb-2">
            <span v-html="highlightIcon(item.icon)" class="w-5 h-5" />
          </div>
          <p class="text-xs font-bold leading-tight">{{ item.title }}</p>
          <p class="text-[10px] opacity-75 mt-0.5">{{ item.desc }}</p>
        </div>
      </div>

      <!-- 明星护工 -->
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-base font-bold text-slate-900">明星护工</h2>
        <span class="text-xs text-slate-400">持证 · 严选入驻</span>
      </div>

      <div v-if="homeLoading" class="text-center text-sm text-slate-400 py-6 mb-6">加载明星护工...</div>
      <div v-else-if="caregivers.length === 0" class="text-center text-sm text-slate-400 py-6 mb-6">
        暂无明星护工数据，完成订单并积累评价后将自动展示
      </div>
      <div v-else class="flex gap-3 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-hide mb-6">
        <article
          v-for="cg in caregivers"
          :key="cg.id"
          class="flex-shrink-0 w-[260px] bg-white rounded-2xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.05)] border border-slate-100/80 cursor-pointer active:scale-[0.98] transition-transform"
          @click="openCaregiver(cg)"
        >
          <div class="flex items-start gap-3">
            <div
              v-if="cg.avatar"
              class="flex-shrink-0 w-14 h-14 rounded-2xl overflow-hidden shadow-md"
            >
              <img :src="resolveUrl(cg.avatar)" class="w-full h-full object-cover" alt="" />
            </div>
            <div
              v-else
              class="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white text-xl font-bold shadow-md"
              :class="cg.avatarBg"
            >
              {{ cg.name.charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-bold text-slate-900 truncate">{{ cg.name }}</h3>
                <span
                  class="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white"
                  :class="cg.levelColor"
                >
                  {{ cg.level }}
                </span>
              </div>
              <p class="text-[11px] text-slate-400 mt-0.5">{{ cg.hospital }} · {{ cg.experience }}经验</p>
              <div class="flex items-center gap-2 mt-1.5">
                <div class="flex items-center gap-0.5">
                  <svg class="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span class="text-xs font-bold text-amber-600">{{ cg.rating }}</span>
                </div>
                <span class="text-[10px] text-slate-300">|</span>
                <span class="text-[11px] text-slate-500">{{ cg.orders }} 单</span>
              </div>
            </div>
          </div>
          <p class="text-xs text-slate-500 mt-3 leading-relaxed line-clamp-2">{{ cg.intro }}</p>
          <div class="flex flex-wrap gap-1.5 mt-3">
            <span
              v-for="tag in cg.tags"
              :key="tag"
              class="px-2 py-0.5 rounded-md bg-slate-50 text-[10px] text-slate-500"
            >
              {{ tag }}
            </span>
          </div>
        </article>
      </div>

      <!-- 家属好评 -->
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-base font-bold text-slate-900">家属好评</h2>
        <div class="flex items-center gap-1">
          <svg class="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <span class="text-xs font-semibold text-slate-600">{{ overallRating }} 综合评分</span>
        </div>
      </div>

      <div v-if="homeLoading" class="py-8 text-center text-sm text-slate-400">加载评价中...</div>
      <div v-else-if="reviews.length === 0" class="py-8 text-center text-sm text-slate-400">暂无评价，完成服务后可在此展示</div>
      <div v-else class="space-y-3">
        <article
          v-for="review in reviews"
          :key="review.id"
          class="bg-white rounded-xl p-4 shadow-[0_2px_12px_rgb(0,0,0,0.04)]"
        >
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-center gap-2 min-w-0">
              <div class="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-xs font-bold text-family-blue">
                {{ (review.userLabel || "用").charAt(0) }}
              </div>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-slate-800 truncate">{{ review.userLabel }}</p>
                <p class="text-[10px] text-slate-400 truncate">{{ review.hospital }}</p>
              </div>
            </div>
            <div class="flex-shrink-0 flex items-center gap-0.5">
              <svg
                v-for="n in review.rating"
                :key="n"
                class="w-3 h-3 text-amber-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
          </div>
          <p class="text-xs text-slate-600 leading-relaxed">{{ review.content }}</p>
          <p class="text-[10px] text-slate-300 mt-2">{{ review.date }} · 护工 {{ review.caregiverName }}</p>
        </article>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { familyHomeApi } from "./familyHomeApi";
import {
  HOME_STATS,
  FEATURED_CAREGIVERS,
  USER_REVIEWS,
  SERVICE_HIGHLIGHTS,
} from "./familyHomeData";
import heroBanner from "../../assets/images/family-hero-banner.png";

const HIGHLIGHT_ICONS = {
  dispatch:
    '<svg class="w-5 h-5 text-family-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>',
  camera:
    '<svg class="w-5 h-5 text-caregiver-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/></svg>',
  cert:
    '<svg class="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>',
};

export default {
  name: "FamilyHomeTab",
  props: {
    userName: {
      type: String,
      default: "用户",
    },
  },
  data() {
    return {
      heroBanner,
      homeLoading: true,
      stats: HOME_STATS,
      caregivers: [],
      reviews: [],
      overallRating: "5.0",
      highlights: SERVICE_HIGHLIGHTS,
    };
  },
  mounted() {
    this.loadHome();
  },
  methods: {
    async loadHome() {
      this.homeLoading = true;
      try {
        const data = await familyHomeApi.load();
        this.stats = data.stats && data.stats.length ? data.stats : HOME_STATS;
        this.caregivers = data.starCaregivers || [];
        this.reviews = data.latestReviews || [];
        this.overallRating = data.overallRating || "5.0";
      } catch (e) {
        console.warn("home data fallback", e);
        this.useMockFallback = true;
        this.stats = HOME_STATS;
        this.caregivers = FEATURED_CAREGIVERS;
        this.reviews = USER_REVIEWS;
        this.overallRating = "4.9";
      } finally {
        this.homeLoading = false;
      }
    },
    resolveUrl(url) {
      if (!url) return "";
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      return url;
    },
    openCaregiver(cg) {
      if (!cg || !cg.id) return;
      this.$router.push({
        path: `/family/caregiver/${cg.id}`,
        query: { fromTab: "home" },
      });
    },
    highlightIcon(name) {
      return HIGHLIGHT_ICONS[name] || "";
    },
  },
};
</script>

<style scoped>
.family-home {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.family-tab-header {
  flex-shrink: 0;
}

.family-tab-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 略增高 Banner，锚点偏右上，避免裁切护工面部 */
.hero-banner {
  min-height: 168px;
}
.hero-banner-text {
  min-height: 168px;
}
.hero-banner-img {
  object-fit: cover;
  object-position: 72% 28%;
}

.family-home-scroll-pad {
  padding-bottom: calc(4.5rem + env(safe-area-inset-bottom));
}
</style>
